// Dispute detail — single dispute timeline + sidebar actions. Drives every
// mutation on /admin/disputes/:id (transitions, messages, evidence, assign,
// refund-recorded). Same component is hit from both nav routes (Disputes
// and Claims) since they share a backing table.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DetailLayout, DetailSection, DefRow } from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  assignDispute,
  attachDisputeEvidence,
  fetchAdminDispute,
  messageDispute,
  recordDisputeRefund,
  transitionDispute,
  type AdminDisputeEvent,
  type DisputeResolution,
  type DisputeStatus,
} from '@/lib/admin-api';

const RESOLUTION_LABELS: Record<DisputeResolution, string> = {
  refunded: 'Refund (full)',
  partial_refund: 'Partial refund',
  no_action: 'No action',
  vendor_charged: 'Charge vendor',
  customer_charged: 'Charge customer',
};

const STATUS_LABELS: Record<DisputeStatus, string> = {
  open: 'Open',
  evidence_pending: 'Request evidence',
  under_review: 'Move to review',
  escalated_to_carrier: 'Escalate to carrier',
  resolved: 'Resolve',
  rejected: 'Reject',
  withdrawn: 'Withdraw',
};

function formatAmount(cents: number | null, currency: string | null): string {
  if (cents == null || currency == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function eventLabel(e: AdminDisputeEvent): string {
  switch (e.kind) {
    case 'message':
      return 'Message';
    case 'status_change':
      return e.statusFrom ? `Status: ${e.statusFrom} → ${e.statusTo}` : `Status set to ${e.statusTo}`;
    case 'evidence':
      return e.mediaFilename ? `Evidence: ${e.mediaFilename}` : 'Evidence attached';
    case 'assignment':
      return 'Assignment';
    case 'resolution':
      return `Resolved → ${e.statusTo ?? 'resolved'}`;
    case 'refund_recorded':
      return 'Stripe refund recorded';
    default:
      return e.kind;
  }
}

export function DisputeDetail() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'disputes', 'detail', id],
    queryFn: ({ signal }) => fetchAdminDispute(id, signal),
    enabled: id.length > 0,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'disputes'] });
  };

  const transitionMut = useMutation({ mutationFn: transitionDispute, onSuccess: invalidate });
  const messageMut = useMutation({
    mutationFn: ({ body }: { body: string }) => messageDispute(id, body),
    onSuccess: invalidate,
  });
  const evidenceMut = useMutation({
    mutationFn: ({ mediaId, note }: { mediaId: number; note?: string }) =>
      attachDisputeEvidence(id, mediaId, note),
    onSuccess: invalidate,
  });
  const assignMut = useMutation({
    mutationFn: ({ adminUserId }: { adminUserId: number | null }) => assignDispute(id, adminUserId),
    onSuccess: invalidate,
  });
  const refundMut = useMutation({
    mutationFn: ({ stripeRefundId }: { stripeRefundId: string }) =>
      recordDisputeRefund(id, stripeRefundId),
    onSuccess: invalidate,
  });

  const [draft, setDraft] = useState('');

  if (isLoading) {
    return (
      <DetailLayout title="Loading…" backTo="/disputes" backLabel="Disputes">
        <div className="text-[12.5px] text-[color:var(--bo-mute)]">Loading dispute…</div>
      </DetailLayout>
    );
  }
  if (error) {
    return (
      <DetailLayout
        title="Couldn't load dispute"
        sub={(error as Error).message}
        backTo="/disputes"
        backLabel="Disputes"
      >
        <div />
      </DetailLayout>
    );
  }
  if (!data) return null;

  const { dispute: d, events } = data;
  const mutationError =
    transitionMut.error || messageMut.error || evidenceMut.error || assignMut.error || refundMut.error;
  const busy =
    transitionMut.isPending ||
    messageMut.isPending ||
    evidenceMut.isPending ||
    assignMut.isPending ||
    refundMut.isPending;

  const handleTransition = (to: DisputeStatus) => {
    if (to === 'resolved') {
      const resolution = window.prompt(
        `Resolution? One of: ${Object.keys(RESOLUTION_LABELS).join(', ')}`,
      );
      if (!resolution || !(resolution in RESOLUTION_LABELS)) return;
      const amountStr =
        resolution === 'refunded' || resolution === 'partial_refund' || resolution.includes('charged')
          ? window.prompt('Decided amount (minor units, e.g. 12345 = $123.45). Leave empty for none:') ?? ''
          : '';
      const decidedAmountCents =
        amountStr.trim() !== '' && Number.isFinite(Number(amountStr)) ? Number(amountStr) : undefined;
      const reason = window.prompt('Reason / note (optional):') ?? undefined;
      transitionMut.mutate({
        id,
        to,
        resolution: resolution as DisputeResolution,
        decidedAmountCents,
        currency: d.currency ?? undefined,
        reason,
      });
      return;
    }
    if (to === 'escalated_to_carrier') {
      const carrier = window.prompt('Carrier name:');
      if (!carrier) return;
      const carrierRef = window.prompt('Carrier case ref (optional):') ?? undefined;
      const reason = window.prompt('Reason / note (optional):') ?? undefined;
      transitionMut.mutate({ id, to, carrier, carrierRef, reason });
      return;
    }
    const reason = window.prompt('Reason / note (optional):') ?? undefined;
    transitionMut.mutate({ id, to, reason });
  };

  const handleAssign = () => {
    const raw = window.prompt('Admin user ID to assign (blank to unassign):');
    if (raw === null) return;
    const trimmed = raw.trim();
    if (trimmed === '') {
      assignMut.mutate({ adminUserId: null });
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) {
      window.alert('Invalid admin user ID.');
      return;
    }
    assignMut.mutate({ adminUserId: n });
  };

  const handleEvidence = () => {
    const raw = window.prompt('Media ID (cms.media.id) to attach as evidence:');
    if (!raw) return;
    const n = Number(raw.trim());
    if (!Number.isFinite(n) || n <= 0) {
      window.alert('Invalid media ID.');
      return;
    }
    const note = window.prompt('Note about this evidence (optional):') ?? undefined;
    evidenceMut.mutate({ mediaId: n, note });
  };

  const handleRefundRecorded = () => {
    const sr = window.prompt('Stripe refund ID (e.g. re_1Abc…):');
    if (!sr) return;
    refundMut.mutate({ stripeRefundId: sr.trim() });
  };

  const sendMessage = () => {
    const body = draft.trim();
    if (!body) return;
    messageMut.mutate(
      { body },
      {
        onSuccess: () => setDraft(''),
      },
    );
  };

  const canRecordRefund =
    d.status === 'resolved' &&
    (d.resolution === 'refunded' || d.resolution === 'partial_refund') &&
    !d.stripeRefundId;

  return (
    <DetailLayout
      eyebrow={d.kind}
      title={d.title}
      sub={
        d.bookingReference
          ? `Booking ${d.bookingReference}`
          : `Booking #${d.bookingId}`
      }
      backTo="/disputes"
      backLabel="Disputes"
      actions={
        <>
          {d.allowedTransitions.map((to) => (
            <RowAction
              key={to}
              kind={to === 'resolved' ? 'primary' : to === 'rejected' || to === 'withdrawn' ? 'danger' : undefined}
              busy={busy}
              onClick={() => handleTransition(to)}
            >
              {STATUS_LABELS[to]}
            </RowAction>
          ))}
          <RowAction busy={busy} onClick={handleAssign}>
            Assign
          </RowAction>
          <RowAction busy={busy} onClick={handleEvidence}>
            Attach evidence
          </RowAction>
          {canRecordRefund ? (
            <RowAction kind="primary" busy={busy} onClick={handleRefundRecorded}>
              Record refund
            </RowAction>
          ) : null}
          <StatusPill status={d.status} />
          {d.resolution ? <StatusPill status={d.resolution} /> : null}
        </>
      }
    >
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Action failed: {(mutationError as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <DetailSection title="Parties">
          <DefRow label="Filed by">
            {d.filedByName ?? d.filedByEmail ?? '—'}
            <span className="ms-2 text-[11.5px] text-[color:var(--bo-mute)]">
              ({d.filedByRole})
            </span>
          </DefRow>
          <DefRow label="Against">{d.againstName ?? d.againstEmail ?? '—'}</DefRow>
          <DefRow label="Assigned admin">{d.assignedAdminEmail ?? 'Unassigned'}</DefRow>
          <button
            type="button"
            onClick={() =>
              void navigate({
                to: '/bookings/$reference' as never,
                params: { reference: d.bookingReference ?? d.bookingId } as never,
              })
            }
            className="mt-3 text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            View booking →
          </button>
        </DetailSection>

        <DetailSection title="Claim">
          <DefRow label="Kind">{d.kind}</DefRow>
          <DefRow label="Amount claimed">{formatAmount(d.amountCents, d.currency)}</DefRow>
          <DefRow label="Decided amount">{formatAmount(d.decidedAmountCents, d.currency)}</DefRow>
          {d.deductibleCents != null ? (
            <DefRow label="Deductible">{formatAmount(d.deductibleCents, d.currency)}</DefRow>
          ) : null}
          {d.carrier ? <DefRow label="Carrier">{d.carrier}</DefRow> : null}
          {d.carrierRef ? (
            <DefRow label="Carrier ref">
              <span className="font-mono text-[12px]">{d.carrierRef}</span>
            </DefRow>
          ) : null}
          {d.stripeRefundId ? (
            <DefRow label="Stripe refund">
              <span className="font-mono text-[12px]">{d.stripeRefundId}</span>
            </DefRow>
          ) : null}
          <DefRow label="Payout hold">{d.payoutHold ? 'Yes' : 'No'}</DefRow>
        </DetailSection>

        <DetailSection title="Timestamps">
          <DefRow label="Opened">{formatDateTime(d.openedAt)}</DefRow>
          <DefRow label="Updated">{formatDateTime(d.updatedAt)}</DefRow>
          <DefRow label="Resolved">{formatDateTime(d.resolvedAt)}</DefRow>
        </DetailSection>
      </div>

      <DetailSection title="Original description">
        <p className="whitespace-pre-wrap text-[13px] text-[color:var(--bo-ink)]">
          {d.description}
        </p>
      </DetailSection>

      <DetailSection title="Timeline">
        {events.length === 0 ? (
          <div className="text-[12.5px] text-[color:var(--bo-mute)]">No events yet.</div>
        ) : (
          <ol className="relative ms-3 space-y-4 border-s border-[color:var(--bo-line)] ps-5">
            {events.map((e) => (
              <li key={e.id}>
                <span className="absolute -ms-[5.5px] mt-1.5 inline-block h-2 w-2 rounded-full bg-[color:var(--bo-accent)]" />
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-[12.5px] font-semibold">{eventLabel(e)}</div>
                  <div className="text-[11px] text-[color:var(--bo-mute)] tabular-nums">
                    {formatDateTime(e.createdAt)}
                  </div>
                </div>
                <div className="text-[11.5px] text-[color:var(--bo-mute)]">
                  {e.actorEmail ?? (e.actorRole === 'system' ? 'system' : `${e.actorRole}`)}
                </div>
                {e.body ? (
                  <p className="mt-1 whitespace-pre-wrap text-[12.5px] text-[color:var(--bo-ink)]">
                    {e.body}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}

        <div className="mt-4 rounded-lg border border-[color:var(--bo-line)] bg-white p-3">
          <label
            htmlFor="dispute-message"
            className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-[color:var(--bo-mute)]"
          >
            Add message
          </label>
          <textarea
            id="dispute-message"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Note for the dispute timeline (visible only to admins for now)…"
            rows={3}
            className="mt-2 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 py-1.5 text-[13px] focus:border-[color:var(--bo-accent)] focus:outline-none"
          />
          <div className="mt-2 flex justify-end">
            <RowAction
              kind="primary"
              busy={messageMut.isPending}
              onClick={sendMessage}
            >
              Post message
            </RowAction>
          </div>
        </div>
      </DetailSection>
    </DetailLayout>
  );
}
